/**
 * Shared recharts configuration: one palette, one tooltip, one axis style for
 * the whole dashboard.
 *
 * Colours are *resolved strings*, not `var()` references: recharts writes SVG
 * presentation attributes, where `var()` support is inconsistent. They are no
 * longer a hand-maintained duplicate of the theme though — every value below is
 * read out of the CSS custom properties in `index.css` at runtime, so a chart
 * can never drift away from the rest of the UI.
 *
 * The reads sit behind getters and a memo, which means they happen the first
 * time something actually renders — by then `.dark` is on `<html>` and the
 * stylesheet is in the document.
 *
 * The categorical palette (`--jv-chart-1..8`) is chosen to stay distinguishable
 * under deuteranopia/protanopia, which a rainbow palette does not.
 */

/* The module's whole job is reading custom properties, so it declares the
   dependency on the stylesheet that defines them: no consumer should have to
   know that importing this file without `index.css` in the document yields
   empty strings. Vite dedupes it with the import in `main.tsx`; the bundle is
   unchanged. */
import "../index.css";

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { TooltipContentProps } from "recharts";

const varCache = new Map<string, string>();

/** Read a CSS custom property off `<html>`, once per page load. */
function cssVar(name: string): string {
  let v = varCache.get(name);
  if (v === undefined) {
    v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    // An empty result means the stylesheet is not in the document yet. Do not
    // memoise that, or the palette stays empty for the rest of the session.
    if (v === "") return v;
    varCache.set(name, v);
  }
  return v;
}

/**
 * A token plus an alpha, as an `rgba()` string an SVG attribute accepts.
 * Kept as a conversion rather than a second token so the accent still has
 * exactly one source. Handles `#rgb` / `#rrggbb` and `rgb()` / `rgba()`.
 */
function withAlpha(color: string, alpha: number): string {
  const value = color.trim();
  if (value.startsWith("#")) {
    const digits = value.slice(1);
    const full =
      digits.length === 3
        ? digits
            .split("")
            .map((c) => c + c)
            .join("")
        : digits;
    const packed = Number.parseInt(full.slice(0, 6), 16);
    if (Number.isNaN(packed)) return value;
    return `rgba(${(packed >> 16) & 255}, ${(packed >> 8) & 255}, ${packed & 255}, ${alpha})`;
  }
  const channels = value.match(/-?\d*\.?\d+/g);
  if (channels && channels.length >= 3) {
    return `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${alpha})`;
  }
  return value;
}

const CHART_VARS = [
  "--jv-chart-1",
  "--jv-chart-2",
  "--jv-chart-3",
  "--jv-chart-4",
  "--jv-chart-5",
  "--jv-chart-6",
  "--jv-chart-7",
  "--jv-chart-8",
] as const;

/**
 * The categorical series palette. An ordinary array as far as callers are
 * concerned (index it, `.map` it, spread it) — each slot is a getter, so the
 * token is read the first time that colour is actually needed.
 */
const palette: string[] = [];
CHART_VARS.forEach((name, i) => {
  Object.defineProperty(palette, i, {
    get: () => cssVar(name),
    enumerable: true,
  });
});

export const CHART_COLORS: readonly string[] = palette;

/** Semantic chart tones, mirroring the semantic Tailwind colours. */
export const CHART_TONE = {
  get fg() {
    return cssVar("--jv-text");
  },
  get muted() {
    return cssVar("--jv-muted");
  },
  get line() {
    return cssVar("--jv-line");
  },
  get surface() {
    return cssVar("--jv-surface");
  },
  get accent() {
    return cssVar("--jv-accent");
  },
  get money() {
    return cssVar("--jv-money");
  },
  get business() {
    return cssVar("--jv-business");
  },
  get ok() {
    return cssVar("--jv-ok");
  },
  get warn() {
    return cssVar("--jv-warn");
  },
  get danger() {
    return cssVar("--jv-danger");
  },
  /** Neutral informational severity — the tone that is not a warning. */
  get info() {
    return cssVar("--jv-info");
  },
};

export const colorAt = (index: number): string =>
  CHART_COLORS[index % CHART_COLORS.length];

/** Prefer the colour the backend assigned (mcc.py owns the category palette). */
export const categoryColor = (given: string | null | undefined, index: number): string =>
  given && /^#|^rgb/.test(given) ? given : colorAt(index);

/** Axis defaults — spread onto <XAxis {...axisProps} />. */
export const axisProps = {
  get stroke() {
    return cssVar("--jv-line");
  },
  get tick() {
    return { fill: cssVar("--jv-muted"), fontSize: 11 };
  },
  tickLine: false,
  get axisLine() {
    return { stroke: cssVar("--jv-line") };
  },
};

export const gridProps = {
  get stroke() {
    return cssVar("--jv-line");
  },
  strokeDasharray: "3 3",
  vertical: false,
};

/** Hover band behind the tooltip: the accent, barely there. */
export const cursorFill = {
  get fill() {
    return withAlpha(cssVar("--jv-accent"), 0.08);
  },
};

interface TooltipRow {
  name: string;
  value: number;
  color: string;
  formatted: string;
}

function TooltipShell({ label, rows }: { label: ReactNode; rows: TooltipRow[] }) {
  return (
    <div className="jv-elevated px-3 py-2 text-xs">
      {label !== null && label !== undefined && label !== "" && (
        <div className="mb-1 font-medium text-fg">{label}</div>
      )}
      <ul className="space-y-0.5">
        {rows.map((row, i) => (
          <li key={`${row.name}-${i}`} className="flex items-center gap-2">
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ background: row.color }}
            />
            <span className="text-muted">{row.name}</span>
            <span className="jv-num ml-auto font-medium text-fg">
              {row.formatted}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface MakeTooltipOptions {
  /** Format a series value. `name` is the series name recharts reports. */
  value: (value: number, name: string) => string;
  /** Format the axis label (usually a day key). */
  label?: (label: string) => string;
  /** Rename a series for display. */
  name?: (name: string) => string;
  /** Hide series whose value is zero (stacked charts get noisy otherwise). */
  hideZero?: boolean;
}

/**
 * Build a `content` renderer for <Tooltip/>. recharts calls this as a plain
 * function, so creating it inline during render is free.
 */
export function makeTooltip(options: MakeTooltipOptions) {
  return function TooltipContent(props: TooltipContentProps): ReactNode {
    if (!props.active || !props.payload || props.payload.length === 0) return null;
    const rows = props.payload
      .map((entry) => {
        const raw = Array.isArray(entry.value) ? entry.value[0] : entry.value;
        const numeric = typeof raw === "number" ? raw : Number(raw ?? 0);
        const name = String(entry.name ?? entry.dataKey ?? "");
        return {
          name: options.name ? options.name(name) : name,
          value: numeric,
          color: entry.color ?? entry.stroke ?? entry.fill ?? CHART_TONE.muted,
          formatted: options.value(numeric, name),
        };
      })
      .filter((row) => !options.hideZero || row.value !== 0);

    if (rows.length === 0) return null;

    const rawLabel = props.label === undefined ? "" : String(props.label);
    const label = options.label && rawLabel ? options.label(rawLabel) : rawLabel;
    return <TooltipShell label={label} rows={rows} />;
  };
}

/** Legend row used next to donut charts (recharts' own legend is too rigid). */
export function LegendList({
  items,
  className = "",
  size = "xs",
}: {
  /** `to` makes the label a drill-down link, the way budget lines link to their transactions. */
  items: { label: string; color: string; value: string; sub?: string; to?: string }[];
  className?: string;
  /** Row type size — `sm` when the legend is the card's main reading surface. */
  size?: "xs" | "sm";
}) {
  const rowText = size === "sm" ? "text-sm" : "text-xs";
  return (
    <ul className={`${size === "sm" ? "space-y-2" : "space-y-1.5"} ${className}`}>
      {items.map((item) => (
        <li key={item.label} className={`flex items-center gap-2 ${rowText}`}>
          {/* Same marker shape as the tooltip's: a legend and the tooltip it
              explains reading as two different chart languages was the point of
              having one chart kit. */}
          <span
            className="size-2.5 shrink-0 rounded-full"
            style={{ background: item.color }}
          />
          {item.to ? (
            <Link
              to={item.to}
              title={`Витрати: ${item.label}`}
              className="min-w-0 flex-1 truncate text-muted transition-colors hover:text-accent"
            >
              {item.label}
            </Link>
          ) : (
            <span className="min-w-0 flex-1 truncate text-muted">{item.label}</span>
          )}
          <span className="jv-num shrink-0 font-medium text-fg">{item.value}</span>
          {item.sub && (
            <span className="jv-num w-9 shrink-0 text-right text-muted sm:w-12">
              {item.sub}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Short axis label for money: 12 400 -> "12,4к". Axes have no room for "₴". */
export function axisMoney(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000)
    return `${(value / 1_000_000).toLocaleString("uk-UA", { maximumFractionDigits: 1 })}млн`;
  if (abs >= 1000)
    return `${(value / 1000).toLocaleString("uk-UA", { maximumFractionDigits: 1 })}к`;
  return String(Math.round(value));
}

/** Trailing N-day moving average, aligned to the end of the window. */
export function movingAverage(values: number[], window: number): (number | null)[] {
  return values.map((_, i) => {
    if (i < window - 1) return null;
    let sum = 0;
    for (let k = i - window + 1; k <= i; k += 1) sum += values[k];
    return sum / window;
  });
}
